import { SortDirection } from 'constants/SortDirection';
import { makeObservable, observable, runInAction } from 'mobx';
import log from 'services/helpers/log';
import { destroy, EntityName, get, getCount, getDetailsForCorrectPagination, getPage, head, post } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import ExtensionStore, { IExtension } from 'stores/extension/ExtensionStore';
import { IGetDataParams } from '../../types';

const SEARCH_FIELDS = ['name', 'outputString'];

export interface IExtensionServerError {
    details: { line: number; message: string }[];
}

export default class ExtensionListStore extends BaseListStore<ExtensionStore> {
    activeIds = [];
    defaultFilter = {
        deleted: false
    };

    get activeExtensions(): IExtension[] {
        return this.activeIds.map((id) => this.data.get(id)?.extension);
    }

    constructor() {
        super({
            itemStore: ExtensionStore,
            sortBy: 'name',
            sortDirection: SortDirection.ASC
        });

        makeObservable(this, {
            activeIds: observable
        });
    }

    async getExtensions(args = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);

        const filter = {
            ...this.defaultFilter,
            ...this.filter
        };

        const conditions = getConditions(filter);

        const includeFields = ['id', 'name', 'active', 'isDefault', 'description', 'type', 'updatedOn', 'versionCount', 'adminOnly', 'createdBy'];

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: [this.sort, `name ${this.sortDirection}`],
                includeFields,
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };

        const { data } = await getPage(EntityName.EXTENSIONS, options);
        const totalCount = await getCount(EntityName.EXTENSIONS, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getExtensionsForScan(args = {}): Promise<void> {
        const shouldReset = this._updateListState(args);

        const filter = {
            ...this.defaultFilter,
            ...this.filter
        };

        const conditions = getConditions(filter);

        const includeFields = ['id', 'name', 'active'];

        const options = {
            filter: {
                skip: this.skip,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: [this.sort, `name ${this.sortDirection}`],
                includeFields
            }
        };

        const { data } = await getPage(EntityName.EXTENSIONS, options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);
    }

    async getExtensionsForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const params = {
            filter: {
                ...this.defaultFilter,
                ...(filter || this.filter)
            },
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            searchFields: SEARCH_FIELDS
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<ExtensionStore>({
            params,
            entityName: EntityName.EXTENSIONS
        });

        this.updateStateForCorrectPagination<ExtensionStore>({ args, data, totalCount });
    }

    async getActiveExtensions(): Promise<void> {
        const { data } = await get(EntityName.EXTENSIONS, {
            filter: {
                where: {
                    deleted: false,
                    active: true,
                    type: 'response'
                }
            }
        });

        this._updateData(data);
        this._updateIds(data, 0, this.activeIds);
    }

    async getExtension(extensionId: string, includeDeleted = false): Promise<void> {
        const options = {
            filter: {
                where: {
                    id: extensionId,
                    deleted: includeDeleted ? undefined : false
                },
                include: [
                    {
                        relation: 'editor',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };

        const { data } = await get(EntityName.EXTENSIONS, options);

        if (data.length) {
            const extension = data[0];
            this._updateData(extension);
        }
    }

    async getExtensionBody(extensionId: string): Promise<void> {
        const {
            data: { body }
        } = await get(`${EntityName.EXTENSIONS}/${extensionId}/latestVersion`);
        return body;
    }

    async saveExtension(extension: IExtension): Promise<IExtensionServerError | undefined> {
        const payload = {
            id: extension.id || undefined,
            description: extension.description,
            name: extension.name,
            type: extension.type,
            body: extension.body,
            severity: extension.severity,
            adminOnly: extension.adminOnly
        };

        let res;

        try {
            res = await post(EntityName.EXTENSIONS, payload);
        } catch (e) {
            return e as IExtensionServerError;
        }

        const { data } = res;

        this._updateData(data);
    }

    async toggleProp(extensionId: string, property: string): Promise<void> {
        const extensionStore = this.data.get(extensionId);
        const extension = extensionStore.extension;
        const newValue = !extension[property];

        await post(EntityName.EXTENSIONS, {
            ...extension,
            [property]: newValue
        });

        extensionStore.update({ [property]: newValue });
    }

    async deleteExtension(extensionId: string): Promise<void> {
        await destroy(`${EntityName.EXTENSIONS}/${extensionId}`);
        await this.getExtensions();
    }

    async resultExists(itemId: string): Promise<boolean | void> {
        try {
            return await head(`/scanExtensionInstances/${itemId}`);
        } catch (e) {
            log.error(e as Error);
            return false;
        }
    }

    resetFilter() {
        this.filter = this.defaultFilter;
    }
}
